/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

import { LogoMultiCardsSectionFragment } from "@cosmos/hygraph-schema";

import {
	BackgroundContainer,
	CosmosButton,
	ResponsiveContainer,
	SectionContainer,
	TextLink,
	TitleDescStack,
} from "@components/elements";
import { css, cx } from "@panda/css";
import { Flex, HStack, VStack } from "@panda/jsx";
import { flex } from "@panda/patterns";

import { useMemo } from "react";
import { LogoMultiCards } from "./LogoMultiCards.molecule";

const multiCardStyles = flex({
	flexDir: "row",
	alignItems: "center",
	justifyContent: "center",
	columnGap: "eds.dimension.40",
	rowGap: "eds.dimension.32",
	flexWrap: "wrap",
	xxlDown: {
		justifyContent: "flex-start",
		columnGap: "eds.dimension.32",
	},
	lgDown: {
		flexDir: "column",
		rowGap: "eds.dimension.32",
		alignItems: "start",
	},
});

const stackStyles = css({
	flexGrow: "1",
	flexShrink: "1",
	flexBasis: "0",
	alignSelf: "stretch",
	justifyContent: "center",
	minW: "360px",
	smDown: {
		minW: "auto",
	},
	"& .cosmos-rich-text.heading": {
		smDown: {
			textStyle: "headingLG",
		},
	},
});

export const LogoMultiCardsSection = ({
	title,
	desc,
	buttons,
	links,
	cards,
	config,
}: LogoMultiCardsSectionFragment): React.JSX.Element => {
	const descriptionColor = useMemo(
		() => (config?.backgroundImage ? "eds.text.primary" : undefined),
		[config?.backgroundImage],
	);

	return (
		<BackgroundContainer
			bgAnimation={config?.backgroundAnimation}
			bgImageAsset={config?.backgroundImage}
			bgFullHeight={config?.backgroundFullHeight}
			edsBackgroundColor={config?.edsBackgroundColor}
			noGradient
		>
			<ResponsiveContainer>
				<SectionContainer disablePaddingBlockEnd={!!config?.disablePaddingBottom}>
					<Flex className={cx("logo-multi-card-section", multiCardStyles)}>
						<VStack alignItems="flex-start" gap="eds.dimension.24" className={stackStyles}>
							<TitleDescStack
								title={{ ...title, variant: "headingXL", as: "h4" }}
								description={{ html: desc, color: descriptionColor }}
							/>
							{buttons?.length ? (
								<HStack gap="eds.dimension.24">
									{(buttons || []).map((btn, i) => (
										<CosmosButton key={`btn-${i}`} {...btn} size="lg" />
									))}
									{(links || []).map((link, i) => {
										if (!link.href || !link.label) return null;

										return <TextLink key={`link-${i}`} {...link} href={link.href} />;
									})}
								</HStack>
							) : null}
						</VStack>
						<LogoMultiCards cards={cards || []} descriptionColor={descriptionColor} />
					</Flex>
				</SectionContainer>
			</ResponsiveContainer>
		</BackgroundContainer>
	);
};
